import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, RefreshCw, CheckCircle, AlertCircle, Clock, Settings, Volume2, VolumeX, Mail, MessageSquare, Smartphone } from 'lucide-react';
import { beesClient } from '../../api/beesClient.js';

interface NotificationItem {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  timestamp: string;
  read: boolean;
  channel: 'email' | 'push' | 'slack' | 'sms';
  priority: 'low' | 'medium' | 'high' | 'critical';
}

interface NotificationSettings {
  emailEnabled: boolean;
  pushEnabled: boolean;
  slackEnabled: boolean;
  smsEnabled: boolean;
  quietHours: {
    enabled: boolean;
    start: string;
    end: string;
  };
}

interface NotificationData {
  notifications: NotificationItem[];
  unreadCount: number;
  settings: NotificationSettings;
  stats: {
    totalSent: number;
    delivered: number;
    failed: number;
    openRate: number;
  };
}

const NotificationPanel: React.FC = () => {
  const [data, setData] = useState<NotificationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNotificationData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await beesClient.runNotification({});
      
      if (response.success && response.data) {
        setData(response.data as NotificationData);
      } else {
        throw new Error(response.error || 'Failed to fetch notification data');
      }
    } catch (err) {
      console.error('Notification API error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch');
      
      setData({
        notifications: [
          {
            id: '1',
            title: 'System Health Alert',
            message: 'CPU usage has exceeded 85% for the past 10 minutes',
            type: 'warning',
            timestamp: '2025-07-03T07:30:00Z',
            read: false,
            channel: 'email',
            priority: 'high'
          },
          {
            id: '2',
            title: 'Deployment Successful',
            message: 'beehive-core v2.1.0 has been successfully deployed to production',
            type: 'success',
            timestamp: '2025-07-03T06:45:00Z',
            read: true,
            channel: 'slack',
            priority: 'medium'
          },
          {
            id: '3',
            title: 'Security Scan Complete',
            message: 'Weekly security scan completed with 0 vulnerabilities found',
            type: 'success',
            timestamp: '2025-07-03T06:00:00Z',
            read: true,
            channel: 'email',
            priority: 'low'
          },
          {
            id: '4',
            title: 'Database Connection Error',
            message: 'Failed to connect to primary database. Failover initiated.',
            type: 'error',
            timestamp: '2025-07-03T05:15:00Z',
            read: false,
            channel: 'sms',
            priority: 'critical'
          },
          {
            id: '5',
            title: 'Backup Completed',
            message: 'Daily backup completed successfully. Size: 2.3GB',
            type: 'info',
            timestamp: '2025-07-03T04:00:00Z',
            read: true,
            channel: 'push',
            priority: 'low'
          }
        ],
        unreadCount: 2,
        settings: {
          emailEnabled: true,
          pushEnabled: true,
          slackEnabled: true,
          smsEnabled: false,
          quietHours: {
            enabled: true,
            start: '22:00',
            end: '08:00'
          }
        },
        stats: {
          totalSent: 1247,
          delivered: 1198,
          failed: 49,
          openRate: 78.5
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotificationData();
  }, []);

  const getNotificationIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'success': return <CheckCircle className="text-green-400" size={16} />;
      case 'warning': return <AlertCircle className="text-yellow-400" size={16} />;
      case 'error': return <AlertCircle className="text-red-400" size={16} />;
      default: return <Bell className="text-blue-400" size={16} />;
    }
  };

  const getChannelIcon = (channel: NotificationItem['channel']) => {
    switch (channel) {
      case 'email': return <Mail size={14} className="text-gray-400" />;
      case 'slack': return <MessageSquare size={14} className="text-gray-400" />;
      case 'sms': return <Smartphone size={14} className="text-gray-400" />;
      default: return <Bell size={14} className="text-gray-400" />;
    }
  };

  const getPriorityColor = (priority: NotificationItem['priority']) => {
    switch (priority) {
      case 'critical': return 'border-l-red-500';
      case 'high': return 'border-l-orange-500';
      case 'medium': return 'border-l-yellow-500';
      default: return 'border-l-blue-500';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <Bell className="text-white" size={32} />
              <h1 className="text-3xl font-bold text-white">Notification Panel</h1>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="glass-dark rounded-xl p-6 border border-white/10 animate-pulse">
                <div className="h-4 bg-slate-700 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-slate-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
          
          <div className="glass-dark rounded-xl p-6 border border-white/10">
            <div className="h-6 bg-slate-700 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-slate-700 rounded animate-pulse"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center space-x-3">
            <Bell className="text-white" size={32} />
            <div>
              <h1 className="text-3xl font-bold text-white">Notification Panel</h1>
              <p className="text-gray-300">Manage system notifications and alerts</p>
            </div>
          </div>
          <motion.button
            onClick={fetchNotificationData}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <RefreshCw className={loading ? 'animate-spin' : ''} size={16} />
            <span>Refresh</span>
          </motion.button>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg"
          >
            <div className="flex items-center space-x-2">
              <AlertCircle className="text-red-400" size={20} />
              <div>
                <div className="text-red-400 font-medium">Notification API Error</div>
                <div className="text-red-300 text-sm">{error}</div>
                <div className="text-red-300 text-sm">Showing mock data for demonstration</div>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass-dark rounded-xl p-6 border border-white/10"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300 text-sm">Total Sent</span>
              <Bell className="text-blue-400" size={20} />
            </div>
            <div className="text-2xl font-bold text-white">{data?.stats.totalSent.toLocaleString()}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="glass-dark rounded-xl p-6 border border-white/10"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300 text-sm">Delivered</span>
              <CheckCircle className="text-green-400" size={20} />
            </div>
            <div className="text-2xl font-bold text-white">{data?.stats.delivered.toLocaleString()}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="glass-dark rounded-xl p-6 border border-white/10"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300 text-sm">Failed</span>
              <AlertCircle className="text-red-400" size={20} />
            </div>
            <div className="text-2xl font-bold text-white">{data?.stats.failed}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="glass-dark rounded-xl p-6 border border-white/10"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300 text-sm">Open Rate</span>
              <Settings className="text-purple-400" size={20} />
            </div>
            <div className="text-2xl font-bold text-white">{data?.stats.openRate}%</div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="lg:col-span-2 glass-dark rounded-xl p-6 border border-white/10"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <Bell className="mr-2" size={20} />
                Recent Notifications
              </h2>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-400">Unread:</span>
                <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">{data?.unreadCount}</span>
              </div>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {data?.notifications.map((notification) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg border-l-4 ${getPriorityColor(notification.priority)} ${
                    notification.read ? 'bg-slate-800/50' : 'bg-slate-700/50'
                  } border border-white/5`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {getNotificationIcon(notification.type)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className={`font-medium ${notification.read ? 'text-gray-300' : 'text-white'}`}>
                            {notification.title}
                          </h3>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm mb-2">{notification.message}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Clock size={12} />
                            <span>{formatTimestamp(notification.timestamp)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {getChannelIcon(notification.channel)}
                            <span className="capitalize">{notification.channel}</span>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            notification.priority === 'critical' ? 'bg-red-900 text-red-300' :
                            notification.priority === 'high' ? 'bg-orange-900 text-orange-300' :
                            notification.priority === 'medium' ? 'bg-yellow-900 text-yellow-300' :
                            'bg-blue-900 text-blue-300'
                          }`}>
                            {notification.priority}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="glass-dark rounded-xl p-6 border border-white/10"
          >
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
              <Settings className="mr-2" size={20} />
              Settings
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Mail size={16} className="text-gray-400" />
                  <span className="text-gray-300">Email</span>
                </div>
                <div className={`w-10 h-6 rounded-full ${data?.settings.emailEnabled ? 'bg-green-500' : 'bg-gray-600'} relative transition-colors`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${data?.settings.emailEnabled ? 'translate-x-5' : 'translate-x-1'}`}></div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Bell size={16} className="text-gray-400" />
                  <span className="text-gray-300">Push</span>
                </div>
                <div className={`w-10 h-6 rounded-full ${data?.settings.pushEnabled ? 'bg-green-500' : 'bg-gray-600'} relative transition-colors`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${data?.settings.pushEnabled ? 'translate-x-5' : 'translate-x-1'}`}></div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <MessageSquare size={16} className="text-gray-400" />
                  <span className="text-gray-300">Slack</span>
                </div>
                <div className={`w-10 h-6 rounded-full ${data?.settings.slackEnabled ? 'bg-green-500' : 'bg-gray-600'} relative transition-colors`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${data?.settings.slackEnabled ? 'translate-x-5' : 'translate-x-1'}`}></div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Smartphone size={16} className="text-gray-400" />
                  <span className="text-gray-300">SMS</span>
                </div>
                <div className={`w-10 h-6 rounded-full ${data?.settings.smsEnabled ? 'bg-green-500' : 'bg-gray-600'} relative transition-colors`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${data?.settings.smsEnabled ? 'translate-x-5' : 'translate-x-1'}`}></div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 mt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {data?.settings.quietHours.enabled ? <VolumeX size={16} className="text-gray-400" /> : <Volume2 size={16} className="text-gray-400" />}
                    <span className="text-gray-300">Quiet Hours</span>
                  </div>
                  <div className={`w-10 h-6 rounded-full ${data?.settings.quietHours.enabled ? 'bg-green-500' : 'bg-gray-600'} relative transition-colors`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${data?.settings.quietHours.enabled ? 'translate-x-5' : 'translate-x-1'}`}></div>
                  </div>
                </div>
                {data?.settings.quietHours.enabled && (
                  <div className="text-sm text-gray-400">
                    {data.settings.quietHours.start} - {data.settings.quietHours.end}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;
